import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from '@/features/auth/hooks/useAuth';
import { LoadingScreen } from '@/shared/ui/components/LoadingScreen';

// ── Lazy-loaded pages ────────────────────────────────────────
const LoginPage          = lazy(() => import('@/pages/auth/LoginPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const SuperAdminLayout   = lazy(() => import('@/pages/super-admin/SuperAdminLayout'));
const TenantLayout       = lazy(() => import('@/pages/tenant/TenantLayout'));

// ── Query Client ─────────────────────────────────────────────
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

// ── Route Guards ─────────────────────────────────────────────
function RequireSuperAdmin({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.systemRole !== 'super_admin') return <Navigate to="/app" replace />;
  return <>{children}</>;
}

function RequireTenant({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.systemRole === 'super_admin') return <Navigate to="/admin" replace />;
  return <>{children}</>;
}

// ── Router ────────────────────────────────────────────────────
function AppRoutes() {
  const { user, loading } = useAuth();
  
  if (loading) return <LoadingScreen />;

  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Auth */}
        <Route path="/login"           element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />

        {/* Super Admin Portal */}
        <Route
          path="/admin/*"
          element={
            <RequireSuperAdmin>
              <SuperAdminLayout />
            </RequireSuperAdmin>
          }
        />

        {/* Tenant Portal */}
        <Route
          path="/app/*"
          element={
            <RequireTenant>
              <TenantLayout />
            </RequireTenant>
          }
        />

        {/* Default Redirect */}
        <Route
          path="/"
          element={
            user
              ? user.systemRole === 'super_admin'
                ? <Navigate to="/admin" replace />
                : <Navigate to="/app" replace />
              : <Navigate to="/login" replace />
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

// ── App Root ──────────────────────────────────────────────────
export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
